import * as React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Backdrop from "@mui/material/Backdrop";
import { AlertComponent } from "../../../common/components";
import type { BackdropProps } from "@mui/material/Backdrop";

import { preNeedForgotPwd } from "../../../api/auth";

import MemorialGoLogo from "../../../assets/media/memgo-logo.svg";
import EmailIcon from "../../../assets/icons/email-placeholder.svg";

import type ForgotPasswordPopupProps from "./forgot-password-popup.props";
import "./forgot-password-popup.scss";

/** Same backdrop pattern as LoginPlannerPopup */
const PlannerBackdrop = React.forwardRef<HTMLDivElement, BackdropProps>(
  function PlannerBackdrop(props, ref) {
    return (
      <Backdrop
        ref={ref}
        {...props}
        className={`preneed-login-backdrop ${props.className ?? ""}`}
      >
        <div className="preneed-login-backdrop__footer">
          © 2026 MemorialGo. All rights reserved.
        </div>
      </Backdrop>
    );
  }
);

const ForgotPasswordPopup: React.FC<ForgotPasswordPopupProps> = ({
  open,
  onClose,
  onBackToLogin,
  defaultEmail = "",
  headerText = "Forgot Password",
  bodyText = "Enter your email to reset your password",
  bodyContent = "We'll send you a link to reset your password",
}) => {
  const [apiLoading, setApiLoading] = React.useState(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [successMsg, setSuccessMsg] = React.useState("");
  const [alertType, setAlertType] = React.useState<
    "success" | "error" | "info" | "warning"
    >("success");



  const initialValues = { email: defaultEmail };

 const validationSchema = Yup.object({
    email: Yup.string()
      .required("Email address is required")
      .max(254, "Email cannot exceed 254 characters")
      .email("Please enter a valid email address")
      .test(
        "local-part-length",
        "Email address cannot exceed 64 characters before @",
        (value) => {
          if (!value) return false;
          const [localPart] = value.split("@");
          return localPart && localPart.length <= 64;
        }
      ),
  });

  const handleSubmit = async (values: { email: string }) => {
    setAlertOpen(false);
    setAlertMessage("");

    try {
        setApiLoading(true);
        const res = await preNeedForgotPwd(values.email);
        if (res?.data?.success) {
            const msg =
                res?.data?.data?.message ||
                "A password reset link has been sent to your registered email address.";
            setSuccessMsg(msg);
            setAlertType("success");
            setAlertMessage(msg);
            setAlertOpen(true);
            } else {
            setSuccessMsg("");
            setAlertType("error");
            setAlertMessage("Unable to send reset link. Please try again.");
            setAlertOpen(true);
        }

    } catch (err: any) {
        setSuccessMsg("");
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong. Please try again.";
        setAlertType("error");
        setAlertMessage(msg);
        setAlertOpen(true);
    } finally {
      setApiLoading(false);
    }
  };

  const handleBackToLogin = () => {
    if (onBackToLogin) return onBackToLogin();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
            //  ignore backdrop clicks
            if (reason === "backdropClick") return;
            onClose();
        }}
      aria-labelledby="forgot-password-dialog-title"
      className="forgot-password-popup-wrapper"
      slots={{ backdrop: PlannerBackdrop }}
    >
      <DialogTitle id="forgot-password-dialog-title">
        <div className="header-wrapper">
          <img
            src={MemorialGoLogo}
            alt="MemorialGo Logo"
            className="memorial-go-logo-img"
          />
          <h4 className="header-text">{headerText}</h4>
        </div>
      </DialogTitle>

      <DialogContent>
        <div className="body-wrapper">
          <p className="body-text">{bodyText}</p>
        </div>
        <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
    />

        <div className="form-wrapper">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, dirty, values, handleChange, handleBlur, touched, errors }) => (
              <Form>
                <div className="form-fields">
                  <div className="body-content-title">Reset Your Password</div>
                  <div className="body-content">{bodyContent}</div>

                  <div className="form-label">
                    Registered Email Address{" "}
                    <span className="form-label-required">*</span>
                  </div>

                  <TextField
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter registered email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <img src={EmailIcon} alt="Email Icon" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    className="form-field"
                  />

                  {/*  API success */}
                  {/* {successMsg && (
                    <div className="success-message">
                        {successMsg}
                    </div>
                    )} */}
                </div>

                <DialogActions>
                  <button
                    className="login-btn"
                    type="submit"
                    disabled={apiLoading || !isValid || !dirty}
                  >
                    {apiLoading ? "Sending..." : "Send Reset Link"}
                  </button>
                </DialogActions>

                {/*  Back to Login below button */}
                <div className="back-to-login-row">
                  <button
                    type="button"
                    className="back-to-login-btn"
                    onClick={handleBackToLogin}
                  >
                    Back to Login
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordPopup;
